import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Avatar,
  TextField,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Select,
  FormControl,
  InputLabel,
  Grid,
} from '@mui/material';
import {
  History as HistoryIcon,
  Search as SearchIcon,
  Home as HomeIcon,
  People as PeopleIcon,
  PersonOutline as PersonIcon,
  FolderOpen as FolderIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import api from '../../utils/api';
import { ActivityLog } from '../../types';
import PageHeader from '../../components/Common/PageHeader';
import ToastNotification from '../../utils/toastNotification';

// ─── Activity metadata ──────────────────────────────────────────────────────

interface ActivityMeta {
  label: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  category: 'property' | 'agent' | 'client' | 'project';
}

const ACTIVITY_META: Record<string, ActivityMeta> = {
  PROPERTY_CREATED: {
    label: 'Property Created',
    icon: <AddIcon fontSize="small" />,
    color: '#2e7d32',
    bgColor: '#e8f5e9',
    category: 'property',
  },
  PROPERTY_UPDATED: {
    label: 'Property Updated',
    icon: <EditIcon fontSize="small" />,
    color: '#1565c0',
    bgColor: '#e3f2fd',
    category: 'property',
  },
  PROPERTY_APPROVED: {
    label: 'Property Approved',
    icon: <CheckCircleIcon fontSize="small" />,
    color: '#2e7d32',
    bgColor: '#e8f5e9',
    category: 'property',
  },
  PROPERTY_REJECTED: {
    label: 'Property Rejected',
    icon: <CancelIcon fontSize="small" />,
    color: '#c62828',
    bgColor: '#ffebee',
    category: 'property',
  },
  PROPERTY_DELETED: {
    label: 'Property Deleted',
    icon: <DeleteIcon fontSize="small" />,
    color: '#c62828',
    bgColor: '#ffebee',
    category: 'property',
  },
  AGENT_CREATED: {
    label: 'Agent Created',
    icon: <AddIcon fontSize="small" />,
    color: '#6a1b9a',
    bgColor: '#f3e5f5',
    category: 'agent',
  },
  AGENT_UPDATED: {
    label: 'Agent Updated',
    icon: <EditIcon fontSize="small" />,
    color: '#6a1b9a',
    bgColor: '#f3e5f5',
    category: 'agent',
  },
  AGENT_DELETED: {
    label: 'Agent Deleted',
    icon: <DeleteIcon fontSize="small" />,
    color: '#c62828',
    bgColor: '#ffebee',
    category: 'agent',
  },
  CLIENT_CREATED: {
    label: 'Client Created',
    icon: <AddIcon fontSize="small" />,
    color: '#e65100',
    bgColor: '#fff3e0',
    category: 'client',
  },
  CLIENT_UPDATED: {
    label: 'Client Updated',
    icon: <EditIcon fontSize="small" />,
    color: '#e65100',
    bgColor: '#fff3e0',
    category: 'client',
  },
  CLIENT_DELETED: {
    label: 'Client Deleted',
    icon: <DeleteIcon fontSize="small" />,
    color: '#c62828',
    bgColor: '#ffebee',
    category: 'client',
  },
  PROJECT_CREATED: {
    label: 'Project Created',
    icon: <AddIcon fontSize="small" />,
    color: '#00695c',
    bgColor: '#e0f2f1',
    category: 'project',
  },
  PROJECT_UPDATED: {
    label: 'Project Updated',
    icon: <EditIcon fontSize="small" />,
    color: '#00695c',
    bgColor: '#e0f2f1',
    category: 'project',
  },
};

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'property': return <HomeIcon sx={{ fontSize: 16 }} />;
    case 'agent':    return <PeopleIcon sx={{ fontSize: 16 }} />;
    case 'client':   return <PersonIcon sx={{ fontSize: 16 }} />;
    case 'project':  return <FolderIcon sx={{ fontSize: 16 }} />;
    default:         return <HistoryIcon sx={{ fontSize: 16 }} />;
  }
};

const getMeta = (type: string): ActivityMeta => {
  return (
    ACTIVITY_META[type] || {
      label: type.replace(/_/g, ' '),
      icon: <HistoryIcon fontSize="small" />,
      color: '#616161',
      bgColor: '#f5f5f5',
      category: 'property',
    }
  );
};

// ─── Stat card ───────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  count: number;
  color: string;
  bgColor: string;
  icon: React.ReactNode;
}

const StatCard: React.FC<StatCardProps> = ({ label, count, color, bgColor, icon }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.10)' },
    }}
  >
    <Avatar sx={{ bgcolor: bgColor, color, width: 48, height: 48 }}>{icon}</Avatar>
    <Box>
      <Typography variant="h5" fontWeight={800} color={color}>
        {count}
      </Typography>
      <Typography variant="body2" color="text.secondary" fontWeight={500}>
        {label}
      </Typography>
    </Box>
  </Paper>
);

// ─── Activity timeline item ───────────────────────────────────────────────────

interface ActivityItemProps {
  activity: ActivityLog;
  isLast: boolean;
}

const ActivityItem: React.FC<ActivityItemProps> = ({ activity, isLast }) => {
  const meta = getMeta(activity.activityType);
  const userInitials = activity.user
    ? `${activity.user.firstName[0]}${activity.user.lastName[0]}`.toUpperCase()
    : '?';

  return (
    <Box sx={{ display: 'flex', gap: 2, position: 'relative' }}>
      {/* Timeline line */}
      {!isLast && (
        <Box
          sx={{
            position: 'absolute',
            left: 23,
            top: 48,
            width: 2,
            bottom: -24,
            bgcolor: 'divider',
            zIndex: 0,
          }}
        />
      )}

      {/* Activity icon circle */}
      <Avatar
        sx={{
          width: 48,
          height: 48,
          bgcolor: meta.bgColor,
          color: meta.color,
          border: '2px solid white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          flexShrink: 0,
          zIndex: 1,
        }}
      >
        {meta.icon}
      </Avatar>

      {/* Content */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          p: 2,
          mb: isLast ? 0 : 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          transition: 'box-shadow 0.2s',
          '&:hover': { boxShadow: '0 2px 10px rgba(0,0,0,0.08)' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          {/* Left: type chip + description */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
              <Chip
                label={meta.label}
                size="small"
                icon={getCategoryIcon(meta.category) as any}
                sx={{
                  bgcolor: meta.bgColor,
                  color: meta.color,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  '& .MuiChip-icon': { color: meta.color },
                }}
              />
            </Box>
            <Typography variant="body2" color="text.primary" sx={{ mt: 0.5, lineHeight: 1.5 }}>
              {activity.description}
            </Typography>
          </Box>

          {/* Right: timestamp */}
          <Tooltip title={format(new Date(activity.createdAt), 'PPpp')} arrow>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', mt: 0.5 }}>
              {formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })}
            </Typography>
          </Tooltip>
        </Box>

        {/* Footer: user info */}
        {activity.user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
            <Avatar
              sx={{
                width: 22,
                height: 22,
                fontSize: '0.65rem',
                fontWeight: 700,
                bgcolor: 'primary.main',
              }}
            >
              {userInitials}
            </Avatar>
            <Typography variant="caption" color="primary.main" fontWeight={600}>
              {activity.user.firstName} {activity.user.lastName}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              •
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {format(new Date(activity.createdAt), 'MMM dd, yyyy  HH:mm')}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'property', label: '🏠 Properties' },
  { value: 'agent', label: '👥 Agents' },
  { value: 'client', label: '👤 Clients' },
  { value: 'project', label: '📁 Projects' },
];

const ActivitiesPage: React.FC = () => {
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    setLoading(true);
    try {
      const res = await api.get('/activities/recent', { params: { limit: 200 } });
      setActivities(res.data);
    } catch (error) {
      console.error('Error loading activities:', error);
      ToastNotification.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  // Statistics
  const stats = useMemo(() => {
    const counts: Record<string, number> = { property: 0, agent: 0, client: 0, project: 0 };
    activities.forEach((a) => {
      const cat = getMeta(a.activityType).category;
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  }, [activities]);

  // Activity type options (dynamic from data)
  const typeOptions = useMemo(() => {
    const types = Array.from(new Set(activities.map((a) => a.activityType)));
    return types.map((t) => ({ value: t, label: getMeta(t).label }));
  }, [activities]);

  // Filtered list
  const filtered = useMemo(() => {
    return activities.filter((a) => {
      const meta = getMeta(a.activityType);
      const matchSearch =
        !search ||
        a.description.toLowerCase().includes(search.toLowerCase()) ||
        (a.user &&
          `${a.user.firstName} ${a.user.lastName}`
            .toLowerCase()
            .includes(search.toLowerCase()));
      const matchCategory = !categoryFilter || meta.category === categoryFilter;
      const matchType = !typeFilter || a.activityType === typeFilter;
      return matchSearch && matchCategory && matchType;
    });
  }, [activities, search, categoryFilter, typeFilter]);

  const paginated = filtered.slice(0, page * PAGE_SIZE);
  const hasMore = paginated.length < filtered.length;

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader
        title="Activity Log"
        subtitle="Track all system actions and changes in real time"
        icon={<HistoryIcon sx={{ fontSize: 'inherit', color: 'primary.main' }} />}
        breadcrumbs={[
          { label: 'Dashboard', path: '/' },
          { label: 'Activities' },
        ]}
      />

      {/* Stats row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Properties"
            count={stats.property ?? 0}
            color="#1565c0"
            bgColor="#e3f2fd"
            icon={<HomeIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Agents"
            count={stats.agent ?? 0}
            color="#6a1b9a"
            bgColor="#f3e5f5"
            icon={<PeopleIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Clients"
            count={stats.client ?? 0}
            color="#e65100"
            bgColor="#fff3e0"
            icon={<PersonIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Projects"
            count={stats.project ?? 0}
            color="#00695c"
            bgColor="#e0f2f1"
            icon={<FolderIcon />}
          />
        </Grid>
      </Grid>

      {/* Filter bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <FilterListIcon color="action" />
        <TextField
          size="small"
          placeholder="Search by description or user..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          sx={{ minWidth: 260, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Category</InputLabel>
          <Select
            label="Category"
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
          >
            {CATEGORY_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Activity Type</InputLabel>
          <Select
            label="Activity Type"
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
          >
            <MenuItem value="">All Types</MenuItem>
            {typeOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {(search || categoryFilter || typeFilter) && (
          <Chip
            label="Clear filters"
            size="small"
            onDelete={() => { setSearch(''); setCategoryFilter(''); setTypeFilter(''); setPage(1); }}
            color="default"
          />
        )}

        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {filtered.length} {filtered.length === 1 ? 'activity' : 'activities'}
        </Typography>
      </Paper>

      {/* Timeline */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <HistoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No activities found
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Try adjusting your filters or check back later.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ position: 'relative' }}>
              {paginated.map((activity, index) => (
                <ActivityItem
                  key={activity.id}
                  activity={activity}
                  isLast={index === paginated.length - 1}
                />
              ))}
            </Box>

            {hasMore && (
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Chip
                  label={`Load more (${filtered.length - paginated.length} remaining)`}
                  onClick={() => setPage((p) => p + 1)}
                  color="primary"
                  variant="outlined"
                  clickable
                  sx={{ px: 2, fontWeight: 600 }}
                />
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ActivitiesPage;
